import { apiClient } from '@/api/client';
import type { PartyFormValues, PartyPricingTier } from '@/schemas';
import type { TenantParty } from '@/api/tenantParties';

/**
 * Server-side party. Mirrors backend `PartyDto.java`.
 *
 * Optional fields may be `null` on the wire — coerce to empty string in
 * the form layer to keep MUI inputs controlled.
 *
 * `mappedTenantParty` is populated server-side by joining the parties
 * row to its TenantParty (matched by GSTIN). It's null when no external
 * vendor app has pushed a matching record yet.
 */
export interface Party extends PartyFormValues {
  id: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  mappedTenantParty?: TenantParty | null;
}

export interface PartyPage {
  content: Party[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/** GET /parties/{id} */
export async function getParty(id: number): Promise<Party> {
  const { data } = await apiClient.get<Party>(`/parties/${id}`);
  return data;
}

/** POST /parties — create one party. */
export async function createParty(payload: PartyFormValues): Promise<Party> {
  const { data } = await apiClient.post<Party>('/parties', payload);
  return data;
}

// ---------------------------------------------------------------------------
// Mapping a tenant-party (external push) to a party in this book.
//
// Used by the "Search & Map Party" column on the parties list:
//   - mapPartyToTenant when the user picks a candidate
//   - unmapPartyFromTenant when the user clears an existing mapping
// ---------------------------------------------------------------------------

/**
 * POST /parties/{partyId}/map?tenantPartyId=...
 * Returns the updated TenantParty (with matchedPartyId now set).
 */
export async function mapPartyToTenant(
  partyId: number,
  tenantPartyId: number,
): Promise<TenantParty> {
  const { data } = await apiClient.post<TenantParty>(
    `/parties/${partyId}/map`,
    null,
    { params: { tenantPartyId } },
  );
  return data;
}

/** DELETE /parties/{partyId}/map?tenantPartyId=... */
export async function unmapPartyFromTenant(
  partyId: number,
  tenantPartyId: number,
): Promise<TenantParty> {
  const { data } = await apiClient.delete<TenantParty>(
    `/parties/${partyId}/map`,
    { params: { tenantPartyId } },
  );
  return data;
}

/**
 * PATCH /parties/{id}/pricing
 * Body: `{ defaultPricing: 'PTR' | 'PTS' }`
 *
 * Drives the PTR/PTS radio on each parties-list row.
 */
export async function updatePartyDefaultPricing(
  partyId: number,
  defaultPricing: PartyPricingTier,
): Promise<Party> {
  const { data } = await apiClient.patch<Party>(
    `/parties/${partyId}/pricing`,
    { defaultPricing },
  );
  return data;
}
